
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from './firebase';
// Firebase Functions will be imported dynamically when needed
import { doc, getDoc } from 'firebase/firestore';

export default function PremiumModal({ isOpen, onClose, reason = 'limit' }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  // Translations now working correctly

  useEffect(() => {
    // Listener per aggiornamenti Premium
    const handlePremiumUpdate = async () => {
      if (auth.currentUser) {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists() && userDoc.data().isPremium) {
          console.log('✅ Stato Premium aggiornato - chiusura modale');
          onClose();
        }
      }
    };

    // Ascolta eventi di aggiornamento Premium
    window.addEventListener('premiumStatusUpdated', handlePremiumUpdate);

    // Controlla localStorage solo una volta all'inizio e poi ogni 5 secondi
    const checkPremiumStatus = () => {
      const premiumStatus = localStorage.getItem('userPremiumStatus');
      if (premiumStatus === 'true') {
        console.log('✅ Premium status rilevato da localStorage');
        onClose();
        return true; // Status trovato, ferma il controllo
      }
      return false;
    };

    // Controllo iniziale
    if (checkPremiumStatus()) return;

    // Controllo periodico meno frequente
    const interval = setInterval(() => {
      if (checkPremiumStatus()) {
        clearInterval(interval);
      }
    }, 5000);

    return () => {
      window.removeEventListener('premiumStatusUpdated', handlePremiumUpdate);
      clearInterval(interval);
    };
  }, [onClose]);

  const handleUpgradeToPremium = async () => {
    if (!auth.currentUser) {
      alert(t('auth.errors.general'));
      return;
    }

    setLoading(true);
    try {
      console.log('🔄 Inizializzazione checkout Stripe...');
      console.log('👤 User ID:', auth.currentUser.uid);

      // Import Firebase Functions dinamicamente
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      
      // Make sure to use the correct region if your functions are deployed in a specific region
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

      console.log('📡 Calling createCheckoutSession function...');

      // Call the Cloud Function with proper error handling
      const result = await createCheckoutSession({
        userId: auth.currentUser.uid
      });

      console.log('✅ Function response:', result);
      console.log('📦 Response data:', result.data);

      if (result.data && result.data.success && result.data.url) {
        console.log('🔗 Redirecting to Stripe Checkout:', result.data.url);
        window.location.href = result.data.url;
      } else if (result.data && result.data.error) {
        console.error('❌ Stripe function error:', result.data.error);
        throw new Error(result.data.error);
      } else {
        console.error('❌ Unexpected response format:', result.data);
        throw new Error('Unexpected response from payment service');
      }
    } catch (error) {
      console.error('❌ Complete error details:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);

      let errorMessage = 'Payment processing error. Please try again.';

      if (error.code === 'functions/unauthenticated') {
        errorMessage = 'You must be logged in to proceed with payment.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = 'Invalid payment data. Please try again.';
      } else if (error.code === 'functions/failed-precondition') {
        errorMessage = 'Payment service is temporarily unavailable.';
      } else if (error.code === 'functions/internal') {
        errorMessage = 'Server error. Please try again in a few minutes.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Payment service not found. Please contact support.';
      } else if (error.message && error.message.includes('CORS')) {
        errorMessage = 'Connection error. Please refresh the page and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.iconContainer}>
          <div style={styles.icon}>✨</div>
        </div>

        <h3 style={styles.title}>
          {t('premium.unlockCreativePotential')}
        </h3>

        <p style={styles.subtitle}>
          {t('premium.limitReached')}
        </p>

        <div style={styles.features}>
          <div style={styles.feature}>
            <span style={styles.checkmark}>✓</span>
            {t('premium.unlimitedWishlists')}
          </div>
          <div style={styles.feature}>
            <span style={styles.checkmark}>✓</span>
            {t('premium.premiumFeatures')}
          </div>
          <div style={styles.feature}>
            <span style={styles.checkmark}>✓</span>
            {t('premium.prioritySupport')}
          </div>
        </div>

        <div style={styles.pricing}>
          <span style={styles.price}>€2.99</span>
          <span style={styles.period}>{t('premium.perMonth')}</span>
        </div>

        <button
          style={styles.upgradeButton}
          onClick={handleUpgradeToPremium}
          disabled={loading}
        >
          {loading ? t('premium.openingPayment') : t('premium.unlockPremium')}
        </button>

        <button style={styles.cancelButton} onClick={onClose}>
          {t('common.cancel')}
        </button>

        <p style={styles.disclaimer}>
          {t('premium.disclaimer')}
        </p>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: 'Poppins, sans-serif',
    padding: '16px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px 20px',
    width: '100%',
    maxWidth: '320px',
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    position: 'relative',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  iconContainer: {
    marginBottom: '16px',
  },
  icon: {
    fontSize: '36px',
    lineHeight: '1',
  },
  title: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '8px',
    color: '#111',
    lineHeight: '1.3',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px',
    lineHeight: '1.4',
  },
  features: {
    marginBottom: '20px',
    textAlign: 'left',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '13px',
    color: '#333',
  },
  checkmark: {
    color: '#10B981',
    fontWeight: 'bold',
    marginRight: '8px',
    fontSize: '14px',
  },
  pricing: {
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: '4px',
  },
  price: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111',
  },
  period: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '500',
  },
  upgradeButton: {
    backgroundColor: '#111',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '12px',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#888',
    border: 'none',
    padding: '10px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: '500',
    width: '100%',
    marginBottom: '12px',
    borderRadius: '8px',
    transition: 'color 0.2s ease',
  },
  disclaimer: {
    fontSize: '11px',
    color: '#999',
    lineHeight: '1.4',
    margin: 0,
  },
};
